/**
 * Niso Blooms - Premium Interactions (2026)
 * Lenis smooth scroll · GSAP ScrollTrigger · SplitType text reveals
 * Custom cursor · Magnetic buttons · Film grain overlay
 */
(function () {
  'use strict';

  var IS_TOUCH = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  var IS_DESKTOP = window.innerWidth >= 960 && !IS_TOUCH;
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Mark JS as loaded - CSS uses .js class for reveal hiding
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  /* ═══════════════════════════════════════════════════════════
     1. LENIS SMOOTH SCROLL
     ═══════════════════════════════════════════════════════════ */
  var lenis;

  function initLenis() {
    if (typeof Lenis === 'undefined' || REDUCED) return;

    lenis = new Lenis({
      duration: 1.2,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      touchMultiplier: 1.5,
    });
    window.lenis = lenis;

    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      (function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      })(performance.now());
    }
  }

  /* ═══════════════════════════════════════════════════════════
     2. GSAP SCROLL-TRIGGERED REVEALS
     ═══════════════════════════════════════════════════════════ */
  function initScrollReveals() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    gsap.registerPlugin(ScrollTrigger);

    if (REDUCED) {
      // Force everything visible immediately
      document.querySelectorAll('[data-reveal]').forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    // [data-reveal="fade-up"] - single element fade + rise
    document.querySelectorAll('[data-reveal="fade-up"]').forEach(function (el) {
      var delay = parseFloat(el.dataset.revealDelay) || 0;
      gsap.from(el, {
        y: 30,
        opacity: 0,
        duration: 0.6,
        delay: delay * 0.6,
        ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 90%', once: true },
      });
    });

    // [data-reveal-stagger] - staggered children reveal
    document.querySelectorAll('[data-reveal-stagger]').forEach(function (container) {
      var items = container.children;
      if (!items.length) return;
      gsap.from(items, {
        y: 60,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: { amount: 0.6, from: 'start' },
        scrollTrigger: { trigger: container, start: 'top 82%', once: true },
      });
    });

    // [data-reveal="clip"] - clip-path image wipe
    document.querySelectorAll('[data-reveal="clip"]').forEach(function (el) {
      gsap.fromTo(el,
        { clipPath: 'inset(100% 0 0 0)' },
        {
          clipPath: 'inset(0% 0 0 0)',
          duration: 1.4,
          ease: 'power4.inOut',
          scrollTrigger: { trigger: el, start: 'top 78%', once: true },
        }
      );
    });

    // [data-parallax] - depth layers
    document.querySelectorAll('[data-parallax]').forEach(function (el) {
      var speed = parseFloat(el.dataset.parallax) || -15;
      gsap.to(el, {
        yPercent: speed,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     3. SPLIT TEXT REVEALS
     ═══════════════════════════════════════════════════════════ */
  function initSplitText() {
    if (typeof gsap === 'undefined' || typeof SplitType === 'undefined' || REDUCED) {
      // Fallback: make text visible
      document.querySelectorAll('[data-split-reveal]').forEach(function (el) {
        el.style.opacity = '1';
      });
      return;
    }

    document.querySelectorAll('[data-split-reveal]').forEach(function (el) {
      var split = new SplitType(el, { types: 'lines' });

      // Wrap each line in overflow:hidden container
      if (split.lines) {
        split.lines.forEach(function (line) {
          var wrapper = document.createElement('div');
          wrapper.style.overflow = 'hidden';
          wrapper.style.display = 'block';
          line.parentNode.insertBefore(wrapper, line);
          wrapper.appendChild(line);
        });
      }

      var delay = parseFloat(el.dataset.splitDelay) || 0;
      var isInHero = !!el.closest('.miso-hero');

      gsap.from(split.lines || [el], {
        yPercent: 110,
        opacity: 0,
        duration: 0.7,
        ease: 'power4.out',
        stagger: 0.08,
        delay: delay * 0.5,
        scrollTrigger: isInHero ? null : { trigger: el, start: 'top 85%', once: true },
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     4. CUSTOM CURSOR (desktop only)
     ═══════════════════════════════════════════════════════════ */
  function initCursor() {
    if (!IS_DESKTOP || REDUCED) return;
    if (typeof gsap === 'undefined') return;

    var ring = document.createElement('div');
    ring.className = 'miso-cursor';
    var dot = document.createElement('div');
    dot.className = 'miso-cursor__dot';
    document.body.appendChild(ring);
    document.body.appendChild(dot);
    document.documentElement.classList.add('has-custom-cursor');

    document.addEventListener('mousemove', function (e) {
      gsap.set(dot, { x: e.clientX, y: e.clientY });
      gsap.to(ring, { x: e.clientX, y: e.clientY, duration: 0.45, ease: 'power2.out' });
    });

    var HOVER_TARGETS = 'a, button, [data-magnetic], input, textarea, select, .miso-fc__card, .miso-occ__tile';

    document.addEventListener('mouseenter', function (e) {
      if (e.target.closest && e.target.closest(HOVER_TARGETS)) {
        gsap.to(ring, { scale: 2.5, opacity: 0.35, duration: 0.3, ease: 'power2.out' });
        gsap.to(dot, { scale: 0.5, duration: 0.3 });
      }
    }, true);

    document.addEventListener('mouseleave', function (e) {
      if (e.target.closest && e.target.closest(HOVER_TARGETS)) {
        gsap.to(ring, { scale: 1, opacity: 1, duration: 0.3, ease: 'power2.out' });
        gsap.to(dot, { scale: 1, duration: 0.3 });
      }
    }, true);

    // Hide cursor when leaving window
    document.addEventListener('mouseleave', function () {
      gsap.to(ring, { opacity: 0, duration: 0.2 });
      gsap.to(dot, { opacity: 0, duration: 0.2 });
    });
    document.addEventListener('mouseenter', function () {
      gsap.to(ring, { opacity: 1, duration: 0.2 });
      gsap.to(dot, { opacity: 1, duration: 0.2 });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     5. MAGNETIC BUTTONS
     ═══════════════════════════════════════════════════════════ */
  function initMagnetic() {
    if (!IS_DESKTOP || typeof gsap === 'undefined') return;

    document.querySelectorAll('[data-magnetic]').forEach(function (btn) {
      var strength = parseFloat(btn.dataset.magnetic) || 0.3;

      btn.addEventListener('mousemove', function (e) {
        var rect = btn.getBoundingClientRect();
        var x = e.clientX - rect.left - rect.width / 2;
        var y = e.clientY - rect.top - rect.height / 2;
        gsap.to(btn, { x: x * strength, y: y * strength, duration: 0.4, ease: 'power2.out' });
      });

      btn.addEventListener('mouseleave', function () {
        gsap.to(btn, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.3)' });
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     6. FILM GRAIN OVERLAY
     ═══════════════════════════════════════════════════════════ */
  function initGrain() {
    if (REDUCED) return;
    var el = document.createElement('div');
    el.className = 'miso-grain';
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
  }

  /* ═══════════════════════════════════════════════════════════
     7. HERO ENTRANCE SEQUENCE
     ═══════════════════════════════════════════════════════════ */
  function initHero() {
    var hero = document.querySelector('.miso-hero');
    if (!hero || typeof gsap === 'undefined') return;

    // Ken Burns on media layer
    var media = hero.querySelector('.miso-hero__media');
    if (media && !REDUCED) {
      gsap.from(media, { scale: 1.05, duration: 1.4, ease: 'power2.out' });
    }
  }

  /* ═══════════════════════════════════════════════════════════
     8. FALLBACK - ensure content visible if libraries fail
     ═══════════════════════════════════════════════════════════ */
  function initFallback() {
    setTimeout(function () {
      var hidden = document.querySelectorAll('[data-reveal], [data-split-reveal]');
      hidden.forEach(function (el) {
        var opacity = parseFloat(window.getComputedStyle(el).opacity);
        if (opacity < 0.1) {
          el.style.transition = 'opacity 0.5s ease';
          el.style.opacity = '1';
          el.style.transform = 'none';
        }
      });
    }, 4000);
  }

  /* ═══════════════════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════════════════ */
  function boot() {
    initLenis();
    initScrollReveals();
    initSplitText();
    initHero();
    initMagnetic();
    initCursor();
    initGrain();
    initFallback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    requestAnimationFrame(boot);
  }
})();
